import type { WritableSignal } from '@angular/core';
import type { PalantirButtonTheme, PalantirButtonVariant } from '../button/button.type';
import type { PalantirIcon } from '../icon/icon.type';

export type PalantirToastComponentType = 'error' | 'info' | 'loading' | 'success' | 'warning';

export interface PalantirToastComponentTheme {
  color: string;
  icon: PalantirIcon;
}

export interface PalantirToastCallToAction {
  /**
   * Função que será chamada quando o botão de ação for clicado.
   * Recebe o próprio toast como parâmetro, permitindo que a ação possa interagir com ele.
   */
  action(toast: PalantirToastCard, event: Event): Promise<void> | void;
  /**
   * Variantes de botão disponíveis para o botão de ação.
   */
  button?: {
    theme?: PalantirButtonTheme;
    variant?: PalantirButtonVariant;
  };
  /**
   * Caso seja `true`, o toast será fechado automaticamente após a ação ser executada.
   */
  closeAfterAction?: boolean;
  /**
   * Ícone do botão de ação que será exibido no toast.
   */
  icon: PalantirIcon;
  /**
   * Texto do botão de ação que será exibido no toast.
   */
  label: string;
}

/**
 * @deprecated Use `PalantirToastCallToAction` instead.
 */
export type LegacyPalantirToastCallToAction = () => Promise<void>;

export type PalantirToastPosition =
  | 'bottom-center'
  | 'bottom-left'
  | 'bottom-right'
  | 'top-center'
  | 'top-left'
  | 'top-right';

export type PalantirToastAnimation = 'fade' | 'slide';

export interface PalantirToastCardInfo {
  animation?: PalantirToastAnimation;
  /**
   * Mostra um botão de ação no toast, que pode ser usado para realizar uma ação específica.
   */
  callToAction?: PalantirToastCallToAction;
  dismissible?: boolean;
  /**
   * @deprecated Use `callToAction` instead.
   */
  func?(): Promise<void>;
  icon?: PalantirIcon;
  id: string;
  message: string;
  messageIsHtml?: boolean;
  pauseOnHover?: boolean;
  position?: PalantirToastPosition;
  progress?: number | 'indeterminate' | null;
  swipeToDismiss?: boolean;
  theme: PalantirToastComponentType;
  timeout: number;
}

export type PalantirToastCardEmitInfo = Omit<PalantirToastCardInfo, 'timeout'> & {
  timeout?: number;
};

export interface PalantirToastCard {
  animation: WritableSignal<PalantirToastAnimation>;
  animationState: WritableSignal<string>;
  /**
   * Mostra um botão de ação no toast, que pode ser usado para realizar uma ação específica.
   */
  callToAction: WritableSignal<PalantirToastCallToAction | null>;
  callToActionLoading: WritableSignal<boolean>;
  dismissible: WritableSignal<boolean>;
  estimatedTime: WritableSignal<string | null>;
  /**
   * @deprecated Use `callToAction` instead.
   */
  func?(): Promise<void>;
  icon: WritableSignal<PalantirIcon | null>;
  id: WritableSignal<string>;
  isToastHovered: WritableSignal<boolean>;
  isToastRemoved: WritableSignal<boolean>;
  message: WritableSignal<string>;
  messageIsHtml: WritableSignal<boolean>;
  pauseOnHover: WritableSignal<boolean>;
  position: WritableSignal<PalantirToastPosition>;
  progress: WritableSignal<number | 'indeterminate' | null>;
  swipeToDismiss: WritableSignal<boolean>;
  theme: WritableSignal<PalantirToastComponentType>;
  timeout: WritableSignal<number>;
}

export const PalantirToastComponentThemes: Record<
  PalantirToastComponentType,
  PalantirToastComponentTheme
> = {
  error: {
    icon: 'error-filled',
    color: '#FC3868',
  },
  success: {
    icon: 'check-filled',
    color: '#12b886',
  },
  warning: {
    icon: 'warning-filled',
    color: '#E2772E',
  },
  info: {
    icon: 'info-filled',
    color: '#135AC0',
  },
  loading: {
    icon: 'loading-outline',
    color: '#135AC0',
  },
};
